const PRESENCE_COLORS = ['#88c0d0', '#a3be8c', '#ebcb8b', '#bf616a', '#b48ead'] as const;

export interface PresenceUser {
  readonly id: string;
  readonly displayName: string;
  readonly color: string;
}

export interface AwarenessPresenceState {
  readonly clientId: number;
  readonly user?: {
    readonly id?: unknown;
    readonly name?: unknown;
    readonly color?: unknown;
  };
}

export function buildPresenceUser(user: {
  readonly id: string;
  readonly githubLogin: string;
}): PresenceUser {
  return {
    id: user.id,
    displayName: user.githubLogin,
    color: getPresenceColor(user.id),
  };
}

export function materializePresenceUsers(input: {
  readonly currentUser: PresenceUser;
  readonly states: readonly AwarenessPresenceState[];
}): PresenceUser[] {
  const users = new Map<string, PresenceUser>();
  addUser(users, input.currentUser);

  for (const state of input.states) {
    if (state.user === undefined) continue;
    const id = nonEmptyString(state.user.id);
    if (id === undefined) continue;
    const displayName = nonEmptyString(state.user.name) ?? 'Someone';
    const color = nonEmptyString(state.user.color) ?? getPresenceColor(id);
    addUser(users, { id, displayName, color });
  }

  const knownDisplayNames = new Set(Array.from(users.values()).map(u => u.displayName));
  for (const state of input.states) {
    if (state.user === undefined || nonEmptyString(state.user.id) !== undefined) continue;
    const displayName = nonEmptyString(state.user.name) ?? 'Someone';
    if (knownDisplayNames.has(displayName)) continue;
    const id = `legacy:${displayName}`;
    const color = nonEmptyString(state.user.color) ?? getPresenceColor(id);
    addUser(users, { id, displayName, color });
    knownDisplayNames.add(displayName);
  }

  return Array.from(users.values()).sort(comparePresenceUsers);
}

function addUser(users: Map<string, PresenceUser>, user: PresenceUser): void {
  if (!users.has(user.id)) {
    users.set(user.id, user);
  }
}

function comparePresenceUsers(a: PresenceUser, b: PresenceUser): number {
  const byName = a.displayName.localeCompare(b.displayName);
  if (byName !== 0) return byName;
  return a.id.localeCompare(b.id);
}

function getPresenceColor(userId: string): string {
  return PRESENCE_COLORS[hashCode(userId) % PRESENCE_COLORS.length];
}

function hashCode(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return h >>> 0;
}

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}
